import { useEffect } from 'react';
import { cn } from '@/src/lib/utils';
import PageBreadcrumb, { Crumb } from './PageBreadcrumb';
import { useSecondaryNav } from './SecondaryNavContext';

export interface SecondaryNavItem {
    name: string;
    href: string;
    icon: any;
    current: boolean;
}

interface SecondarySidebarProps {
    children: React.ReactNode;
    title: string | React.ReactNode;
    subtitle?: string;
    actions?: React.ReactNode;
    filters?: React.ReactNode;
    navigation?: SecondaryNavItem[];
    breadcrumbs?: Crumb[];
}

export default function SecondarySidebar({
    children,
    title,
    subtitle,
    actions,
    navigation = [],
    filters = null,
    breadcrumbs,
}: SecondarySidebarProps) {
    const { setHasSecondaryNav } = useSecondaryNav();

    useEffect(() => {
        if (navigation.length > 0) {
            setHasSecondaryNav(true);
            return () => setHasSecondaryNav(false);
        }
    }, [navigation.length, setHasSecondaryNav]);

    return (
        <div className="flex h-full">
            {/* Desktop sidebar */}
            {navigation.length > 0 && (
                <div className="hidden md:flex w-48 lg:w-56 shrink-0 flex-col border-r bg-muted/30">
                    <div className="flex flex-col gap-1 p-3 pt-6">
                        {navigation.map((item) => (
                            <a
                                key={item.name}
                                href={item.href}
                                className={cn(
                                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                                    item.current
                                        ? 'bg-accent text-accent-foreground'
                                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                                )}
                            >
                                <item.icon
                                    className="size-4 shrink-0"
                                    aria-hidden="true"
                                />
                                {item.name}
                            </a>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex-1 min-w-0 overflow-auto">
                {/* Mobile horizontal nav */}
                {navigation.length > 0 && (
                    <div className="md:hidden border-b bg-background sticky top-0 z-10">
                        <nav className="flex overflow-x-auto px-4 gap-1 py-2 scrollbar-none">
                            {navigation.map((item) => (
                                <a
                                    key={item.name}
                                    href={item.href}
                                    className={cn(
                                        'flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0',
                                        item.current
                                            ? 'bg-foreground text-background'
                                            : 'bg-muted text-muted-foreground',
                                    )}
                                    style={item.current ? {
                                        backgroundColor: 'hsl(var(--foreground))',
                                        color: 'hsl(var(--background))',
                                    } : undefined}
                                >
                                    <item.icon
                                        className="size-3.5 shrink-0"
                                        aria-hidden="true"
                                    />
                                    {item.name}
                                </a>
                            ))}
                        </nav>
                    </div>
                )}
                {title && (
                    <header className="pb-6 pt-8">
                        <div className="flex flex-col gap-3 px-4 sm:px-6 lg:px-8">
                            {breadcrumbs && breadcrumbs.length > 0 && (
                                <PageBreadcrumb crumbs={breadcrumbs} />
                            )}
                            <div className="flex flex-wrap items-end gap-6 sm:flex-nowrap">
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                                    {title}
                                </h1>
                                {subtitle && (
                                    <p className="text-sm leading-6 text-muted-foreground mt-1">
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                            {filters && (
                                <div className="order-last flex w-full gap-x-8 text-sm font-medium leading-6 sm:order-none sm:w-auto sm:border-l sm:border-border sm:pl-6 sm:leading-7">
                                    {filters}
                                </div>
                            )}
                            <div className="flex-1" />
                            {actions}
                            </div>
                        </div>
                    </header>
                )}
                <div className="px-4 sm:px-6 lg:px-8 pb-8">
                    {children}
                </div>
            </div>
        </div>
    );
}
